import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ChevronLeft, ChevronRight, LucideIconData } from 'lucide-angular';
import type { BlogPost } from '../../../../shared/models/blog.model';
import { IconComponent } from '../../../../shared/ui/icon/icon.component';
import { BlogDatePipe } from '../../../blog/pipes/blog-format.pipes';

const SLOT_STEP_FALLBACK = 320;

@Component({
  selector: 'app-game-blog-posts',
  imports: [NgOptimizedImage, RouterLink, IconComponent, BlogDatePipe],
  templateUrl: './game-blog-posts.component.html',
  styleUrl: './game-blog-posts.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class GameBlogPostsComponent {
  posts = input.required<BlogPost[]>();
  gameName = input.required<string>();

  /**
   * «Ver todos» lleva al listado de ESTE juego (B65 del spec del blog). El
   * slug sale de los propios posts, que son todos de la sección: sin input
   * nuevo, `game-section-page` no se toca. Sin posts, al listado completo.
   */
  protected readonly viewAllQueryParams = computed(() => {
    const slug = this.posts()[0]?.game.slug;
    return slug ? { game: slug } : null;
  });

  @ViewChild('track') private trackRef!: ElementRef<HTMLElement>;

  protected readonly chevronLeft: LucideIconData = ChevronLeft;
  protected readonly chevronRight: LucideIconData = ChevronRight;

  protected readonly currentIndex = signal(0);
  protected readonly visibleCount = signal(1);
  protected readonly totalPosts = computed(() => this.posts().length);
  protected readonly maxIndex = computed(() =>
    Math.max(0, this.totalPosts() - this.visibleCount()),
  );

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    afterNextRender(() => {
      const el = this.trackRef?.nativeElement;
      if (!el) return;

      this.updateVisibleCount(el);

      const onScroll = (): void => this.onTrackScroll(el);
      el.addEventListener('scroll', onScroll, { passive: true });

      let resizeObserver: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => this.updateVisibleCount(el));
        resizeObserver.observe(el);
      }

      this.destroyRef.onDestroy(() => {
        el.removeEventListener('scroll', onScroll);
        resizeObserver?.disconnect();
      });
    });
  }

  protected scrollPrev(): void {
    if (!this.isBrowser) return;
    if (this.currentIndex() === 0) {
      this.scrollToIndex(this.maxIndex());
      return;
    }
    this.scrollToIndex(Math.max(0, this.currentIndex() - this.visibleCount()));
  }

  protected scrollNext(): void {
    if (!this.isBrowser) return;
    const next = this.currentIndex() + this.visibleCount();
    if (next > this.maxIndex()) {
      this.scrollToIndex(0);
      return;
    }
    this.scrollToIndex(next);
  }

  private scrollToIndex(index: number): void {
    const el = this.trackRef?.nativeElement;
    if (!el) return;
    const clamped = Math.max(0, Math.min(index, this.maxIndex()));
    const slotStep = this.getSlotStep();
    el.scrollTo({ left: clamped * slotStep, behavior: 'smooth' });
    this.currentIndex.set(clamped);
  }

  private onTrackScroll(el: HTMLElement): void {
    const slotStep = this.getSlotStep();
    const rawIndex = Math.round(el.scrollLeft / slotStep);
    const clamped = Math.max(0, Math.min(rawIndex, this.maxIndex()));
    this.currentIndex.set(clamped);
  }

  private updateVisibleCount(el: HTMLElement): void {
    const slotStep = this.getSlotStep();
    this.visibleCount.set(Math.max(1, Math.floor(el.clientWidth / slotStep)));
  }

  private getSlotStep(): number {
    const track = this.trackRef?.nativeElement;
    const firstSlot = track?.firstElementChild as HTMLElement | null;
    if (!firstSlot) return SLOT_STEP_FALLBACK;
    const gap = parseFloat(getComputedStyle(track).gap) || 0;
    return firstSlot.offsetWidth + gap;
  }
}
